import { AtcObjectRedraw, CartItemQuantity, CartRule, CartRuleResult } from '../types/models';
import { ShopifyCart, ShopifyCartItem } from '../types/servicesShopifyCart';
import { useShopifyCartService } from '../services/shopifyCart';
import { cartRuleVariantParentMaxQuantity } from '../cartRules/variantParentMaxQuantity';
import { cartRuleFreeGift } from '../cartRules/freeGift';
import { cartRuleBundleMaxCartQuantity } from '../cartRules/bundleMaxCartQuantity';
import {
  getProductBySku,
  getVariantIdBySku,
} from '@/features/Product/composables/productLookupHelper';
import { useProductStockStore } from '@/features/Product/stores/productStock';

let isValidatingCart = false;

const applyRuleResult = (
  items: ShopifyCartItem[],
  result: CartRuleResult | null,
): ShopifyCartItem[] => {
  if (!result) return items;
  const removedKeys = new Set(result.remove.map(({ key }) => key));
  const updatedQuantities = new Map(result.update.map(({ key, quantity }) => [key, quantity]));
  return items
    .filter((item) => !removedKeys.has(item.key))
    .map((item) =>
      updatedQuantities.has(item.key)
        ? { ...item, quantity: updatedQuantities.get(item.key) as number }
        : item,
    );
};

export const getCartRuleChanges = (cart: ShopifyCart): CartRuleResult => {
  const cartRules: CartRule[] = [
    cartRuleFreeGift,
    cartRuleVariantParentMaxQuantity,
    cartRuleBundleMaxCartQuantity,
  ];
  const changes: CartRuleResult = { add: [], update: [], remove: [] };
  const addRedraws: AtcObjectRedraw[] = [];
  let workingItems = cart.items;
  cartRules.forEach((rule) => {
    const result = rule({ ...cart, items: workingItems });
    if (!result) return;
    changes.add.push(...result.add);
    changes.update.push(...result.update);
    changes.remove.push(...result.remove);
    if (result.redrawAdd) addRedraws.push(result.redrawAdd);
    workingItems = applyRuleResult(workingItems, result);
  });
  if (addRedraws.length) {
    changes.redrawAdd = (items, variantIds) =>
      addRedraws.reduce(
        (redrawnItems, redraw) =>
          redrawnItems.length ? redraw(redrawnItems, variantIds) : redrawnItems,
        items,
      );
  }
  return changes;
};

export const useCartValidation = () => {
  const { updateCart, addToCart, getCart } = useShopifyCartService();
  const { checkAddStockWithRedraws } = useProductStockStore();

  const toUpdateObject = (items: CartItemQuantity[]) =>
    Object.fromEntries(items.map(({ key, quantity }) => [key, quantity]));

  const validateCart = async (cart: ShopifyCart): Promise<ShopifyCart> => {
    if (isValidatingCart) return cart;
    isValidatingCart = true;
    try {
      const {
        add: itemsToAdd,
        update: itemsToUpdate,
        remove: itemsToRemove,
        redrawAdd,
      } = getCartRuleChanges(cart);

      if (!itemsToAdd.length && !itemsToUpdate.length && !itemsToRemove.length) {
        return cart;
      }

      try {
        if (itemsToRemove.length) {
          await updateCart(toUpdateObject(itemsToRemove));
        }

        if (itemsToUpdate.length) {
          await updateCart(toUpdateObject(itemsToUpdate));
        }

        if (itemsToAdd.length) {
          const { stockCheck, items } = await checkAddStockWithRedraws(itemsToAdd, redrawAdd);
          if (stockCheck === 'available') await addToCart(items);
        }
      } catch (error) {
        console.error('Failed to apply cart rules', error);
      }

      return getCart();
    } finally {
      isValidatingCart = false;
    }
  };

  return {
    validateCart,
    getVariantIdBySku,
    getProductBySku,
  };
};
